import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { TicketForecast } from '@catalyst-psa/shared';

import { ForecastGrid, groupContributions, type ForecastGridRow } from './ForecastGrid.js';
import { useTicketForecast } from './api.js';

interface TicketForecastPanelProps {
  /** When false, the panel does not fetch (e.g. user lacks the tickets permission). */
  enabled?: boolean;
  defaultCollapsed?: boolean;
  /** Pre-fetched forecast (e.g. the public portal). When provided, the panel does not fetch. */
  data?: TicketForecast;
  /** When false, drill-down rows do not navigate (read-only public view). */
  interactive?: boolean;
}

/**
 * Revenue forecast for un-billed ticket (support) hours, grouped by customer and the
 * hour's work-date month. Direct hours forecast at `hours × rate`; managed-services
 * hours count only their monthly overage (included block hours are not new revenue).
 * Drill-down breaks a customer down by its contributing tickets / agreements — clicking
 * a source opens that record (the ticket or the agreement).
 */
export function TicketForecastPanel({
  enabled = true,
  defaultCollapsed,
  data,
  interactive = true,
}: TicketForecastPanelProps): JSX.Element {
  const navigate = useNavigate();
  const query = useTicketForecast(enabled && data === undefined);
  const forecast = data ?? query.data;

  const rows = useMemo<ForecastGridRow[]>(
    () =>
      (forecast?.groups ?? []).map((g) => ({
        key: g.key,
        label: <span>{g.customer}</span>,
        total: g.total,
        totalsByMonth: g.totalsByMonth,
        contributions: groupContributions(g.contributions),
      })),
    [forecast],
  );

  // Source ids are prefixed with their kind (`ticket:<id>` / `agreement:<id>`) so a
  // drill-down row opens the right record: the ticket modal or the agreement detail page.
  const openSource = (sourceId: string): void => {
    const [kind, id] = sourceId.split(':');
    if (!id) return;
    if (kind === 'ticket') navigate(`/tickets/${id}`);
    else if (kind === 'agreement') navigate(`/agreements/${id}`);
  };

  return (
    <ForecastGrid
      title="Ticket revenue forecast"
      subtitle="un-billed hours, by customer"
      months={forecast?.months ?? []}
      rows={rows}
      grandTotal={forecast?.total ?? '0'}
      totalsByMonth={forecast?.totalsByMonth ?? {}}
      onOpenSource={interactive ? openSource : undefined}
      isLoading={data === undefined && query.isLoading}
      isError={data === undefined && query.isError}
      emptyText="No un-billed ticket hours to forecast yet."
      defaultCollapsed={defaultCollapsed}
    />
  );
}
